import { ErrorMessage } from '@hookform/error-message';
import { Add } from '@mui/icons-material';
import {
  Box,
  Chip,
  InputLabel,
  Menu,
  MenuItem,
  Typography,
} from '@mui/material';
import { Theme } from '@mui/material/styles';
import { SxProps } from '@mui/system';
import { ReactNode, useCallback, useMemo, useState } from 'react';
import { FieldErrors } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { ChipList } from '~/components/ChipList';
import { WorkflowStepConfig } from '~/data/workflows';
import { WorkflowConfigFormValues } from '~/scenes/AdminModule/components/Views/WorkflowsConfigurationView/components/WorkflowConfigDialog/component';
import { getWorkflowRolesTranslationKeyFromString } from '~/scenes/AdminModule/components/Views/WorkflowsConfigurationView/data';
import { theme } from '~/theme';

export type WorkflowStepFieldProps = {
  label?: string;
  description?: string;
  stepConfig: WorkflowStepConfig;
  onChange: (stepConfig: WorkflowStepConfig) => void;
  roleOptions: string[];
  lockedOptions?: string[];
  renderAddMenuItem?: (menuItemProps: {
    option: string;
    onSelect: (option: string) => void;
    isSelected: boolean;
    selection: string[];
  }) => ReactNode;
  max?: number;
  disabled?: boolean;
  formErrors?: FieldErrors<WorkflowConfigFormValues>;
  fieldName?: string;
  sx?: SxProps<Theme>;
};

export const WorkflowStepField = (props: WorkflowStepFieldProps) => {
  const { t } = useTranslation();
  const {
    stepConfig,
    onChange,
    roleOptions,
    lockedOptions,
    renderAddMenuItem,
    label,
    description,
    sx,
    disabled,
    formErrors,
    fieldName,
    max,
  } = props;
  const handleAddRole = (option: string) => {
    onChange({
      roleNames: [...stepConfig.roleNames, option],
    });
  };

  const handleDeleteRole = (option: string) => {
    onChange({
      roleNames: stepConfig.roleNames.filter((role) => role !== option),
    });
  };

  const translateRoleName = (roleName: string) =>
    t(getWorkflowRolesTranslationKeyFromString(roleName));

  const allOptionsSelected = stepConfig.roleNames.length === roleOptions.length;
  const maxOptionsSelected = !!max ? max <= stepConfig.roleNames.length : false;
  const hasSelectionsLimitReached = allOptionsSelected || maxOptionsSelected;

  const resolveErrorFieldName = useCallback((fieldName: string) => {
    const [name, index] = fieldName.split('.');
    return !!index ? `${name}[${index}]` : name;
  }, []);

  const errorNames = useMemo(() => {
    if (!!fieldName) {
      const name = resolveErrorFieldName(fieldName);

      return [name, `${name}.roleNames`].filter((key) =>
        Object.keys(formErrors as FieldErrors).includes(key),
      );
    }
    return [];
  }, [fieldName, formErrors, resolveErrorFieldName]);

  const hasError = useMemo(() => {
    return (
      errorNames.filter((key) =>
        Object.keys(formErrors as FieldErrors).includes(key),
      ).length > 0
    );
  }, [errorNames, formErrors]);

  const addOptions = !!lockedOptions
    ? roleOptions.filter(
        (option) =>
          !(
            lockedOptions?.includes(option) &&
            stepConfig.roleNames.includes(option)
          ),
      )
    : roleOptions;

  const AddStepChip = (addStepProps: {
    selection: string[];
    options: string[];
    onSelect: (option: string) => void;
  }) => {
    const { options, onSelect, selection } = addStepProps;

    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);
    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
      setAnchorEl(event.currentTarget);
    };
    const handleClose = () => {
      setAnchorEl(null);
    };

    const handleSelectOption = (option: string) => {
      onSelect(option);
      handleClose();
    };

    return (
      <>
        <Chip onClick={handleClick} label={t('add')} icon={<Add />} />
        <Menu
          id="basic-menu"
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          slotProps={{
            list: {
              'aria-labelledby': 'basic-button',
            },
          }}
        >
          {options.map((option) => {
            if (!!renderAddMenuItem) {
              return renderAddMenuItem({
                option,
                onSelect: handleSelectOption,
                isSelected: selection.includes(option),
                selection,
              });
            }

            return (
              <MenuItem
                key={option}
                onClick={() => handleSelectOption(option)}
                disabled={selection.includes(option)}
              >
                {translateRoleName(option)}
              </MenuItem>
            );
          })}
        </Menu>
      </>
    );
  };

  return (
    <Box
      sx={{
        margin: theme.spacing(1.5, 0),
        ...sx,
      }}
    >
      {!!label && <InputLabel>{label}</InputLabel>}

      <ChipList variant={'outlined'} error={hasError}>
        {stepConfig.roleNames.map((roleName) => {
          const canDelete = !(
            disabled ||
            (!!lockedOptions && lockedOptions.includes(roleName))
          );

          return (
            <Chip
              key={roleName}
              label={translateRoleName(roleName)}
              color={'primary'}
              onDelete={
                canDelete ? () => handleDeleteRole(roleName) : undefined
              }
            />
          );
        })}
        {!disabled && !hasSelectionsLimitReached && (
          <AddStepChip
            options={addOptions}
            onSelect={handleAddRole}
            selection={stepConfig.roleNames}
          />
        )}
      </ChipList>
      {!!description && (
        <Typography variant={'caption'}>{description}</Typography>
      )}
      {errorNames.map((name) => {
        return (
          <ErrorMessage
            key={`error-${name}`}
            errors={formErrors}
            name={name}
            render={({ message }) => (
              <Typography
                variant={'caption'}
                sx={{ color: theme.palette.error.main }}
                component={'p'}
              >
                {message}
              </Typography>
            )}
          />
        );
      })}
    </Box>
  );
};
